import { createContext, useContext, type ReactNode } from 'react';

// Public tenant info exposed to the SPA. NEVER include secrets here (no api keys, no Blesta creds).
// The full TenantConfig lives server-side; we send only what the browser needs.
export interface PublicTenant {
  id: string;
  title: string;
  branding: {
    logoFile: string;
    faviconFile: string;
    brandUrl: string;
    // Optional header action buttons — undefined/missing hides the button.
    profileUrl?: string;
    supportPhone?: string;
    supportEmail?: string;
  };
  locale: { defaultTz: string; defaultLang: 'en' | 'fr' };
  // API key is required by the legacy auth model. Will go away once Phase 3 auth ships.
  apiKey: string;
}

declare global {
  interface Window {
    __TENANT__: PublicTenant | null;
  }
}

const TenantContext = createContext<PublicTenant | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const tenant = typeof window !== 'undefined' ? window.__TENANT__ : null;
  return <TenantContext.Provider value={tenant}>{children}</TenantContext.Provider>;
}

export function useTenant(): PublicTenant {
  const t = useContext(TenantContext);
  if (!t) {
    throw new Error(
      'TenantContext is null. The SPA was not served by apps/api (which injects window.__TENANT__).',
    );
  }
  return t;
}

// Convenience for components that should render a friendly fallback while bootstrapping.
export function useTenantOptional(): PublicTenant | null {
  return useContext(TenantContext);
}
